/**
 * Compile-time constants for the deep-link / protocol-handler router.
 *
 * Adding a host to the allow-list MUST be a code change. There is no runtime
 * setter and no config-driven mutation path; the set is exported as a frozen
 * `ReadonlySet<string>` and the underlying tuple is `as const`. This blocks
 * the entire class of attacks that rely on coaxing the app into trusting a
 * lookalike host — any URL whose `URL.hostname` does not exactly match (after
 * lowercase) returns `{ kind: 'rejectedHost' }` from the parser.
 *
 * THREAT MODEL
 *   The threat surface here is "the OS / another tab hands your app an
 *   attacker-controlled URL." A user clicks a link in a hostile email, the
 *   OS routes it to the PWA via the registered protocol handler, and the
 *   router decides what to do.
 *
 *   The browser's URL parser already converts visually-confusable hostnames
 *   to their punycode (xn--…) form, so `kaеther.fr` (with a Cyrillic 'е')
 *   surfaces as `xn--kaether-c2e.fr` or similar — which is NOT literally
 *   `kaether.fr` and therefore lands in `rejectedHost`. We rely on that
 *   behaviour rather than maintaining our own Unicode confusables table.
 *
 *   The 2048-character URL cap bounds the worst case work the parser has to
 *   do on a hostile input. Real `kaether.fr/c/<call-link>` URLs are well
 *   under 1KB; the cap is a margin, not a tight bound. ReDoS-style amplifi-
 *   cation against the per-route regexes is prevented twice over: the
 *   regexes are linear-time (bounded character classes, no backtracking
 *   ambiguity) and the input length is capped before the regex runs.
 *
 * Service-Worker importability guarantee:
 *   This file uses NO `window`, `document`, or DOM-only APIs. Pure constants
 *   only.
 */

/**
 * Compile-time host allow-list. Exactly one entry today (`kaether.fr`); a
 * brand change is a deliberate edit to this file and a deploy.
 *
 * Comparison is done in lowercase: `URL.hostname` already lowercases the
 * host on construction, but we re-lower in the parser as belt-and-braces
 * against a future runtime where that invariant slips.
 */
export const ALLOWED_HOSTS = new Set<string>(["kaether.fr"]) as ReadonlySet<string>;

/**
 * Upper bound on the length of a URL the parser will examine. URLs longer
 * than this are rejected before any regex / decoder fires.
 *
 * 2048 is comfortably above the longest legitimate kaether.fr deep link
 * (a `kaether:call:link:v1:…` body is ~200 bytes base64url-encoded) and
 * well below the 8KB-ish limit modern browsers enforce on URLs. Pinning
 * the limit here means the parser's worst-case work is bounded regardless
 * of what the OS hands us.
 */
export const MAX_URL_LENGTH = 2048;

/**
 * Custom scheme exposed to the OS / other apps. The spec REQUIRES the
 * `web+` prefix for any scheme that is not on the browser's built-in
 * whitelist — passing bare `kaether` to `registerProtocolHandler` throws
 * a `SecurityError` in every modern browser (Chrome, Firefox, Safari).
 *
 * The actual handler URL the browser fetches when the user clicks a
 * `web+kaether:` URL is set in `DeepLinkRegistration.ts`; this constant is
 * just the scheme name.
 */
export const CUSTOM_SCHEME = "web+kaether" as const;

/**
 * Path the registered protocol handler funnels into. The browser will
 * substitute `%s` with the percent-encoded original `web+kaether:` URL.
 *
 * Funnelling through a single confirmation route guarantees that the OS
 * cannot route a custom-scheme link straight into a side-effect API — the
 * router has to parse and confirm first.
 */
export const HANDLER_URL_TEMPLATE = "/handle-deep-link?u=%s" as const;

/**
 * Path prefixes recognised by the per-route validators in `DeepLinkParser`.
 * Centralised here so a path-rename is one edit.
 */
export const PROFILE_PATH_PREFIX = "/u/" as const;
export const GROUP_INVITE_PATH_PREFIX = "/g/" as const;
export const CALL_LINK_PATH_PREFIX = "/c/" as const;
